/**
 * A printing, named and coloured, in one place.
 *
 * There is no per-variant card art and there is not going to be, so four Base
 * Set Charizards are four slots drawn from ONE image and told apart by a
 * colour-coded badge on the top edge of the tile. The bottom edge belongs to
 * price, which is why the badge is at the top: the two can never fight over the
 * same corner.
 *
 * The colours are fixed hexes rather than theme colours on purpose. A badge
 * sits on top of card art, not on a page background, so it has to read the same
 * in light mode and dark mode; and "Shadowless is a color, 1st Ed is a color"
 * only means anything if it is the SAME colour every time it appears, on every
 * screen that ever shows a printing.
 *
 * A printing is (subtype, stamps, foil, type). `type` - the finish - was left
 * out for a long time on the grounds that holo, normal and reverse are three
 * finishes of one card inside one print run, and folding them in would turn
 * Brilliant Stars into 341 slots instead of 216. It does, and that is now the
 * count.
 *
 * What changed the answer: leaving it out meant the plain card and the reverse
 * holo were the same tile, so a search could not offer both, an owner could
 * not say which one they held, and the reverse quietly wore the plain card's
 * price. Every other layer already knew - the catalogue stores the finish, the
 * cards table has v_type, prices are kept per finish. Only the one layer a
 * person actually taps threw it away.
 *
 * The finish is appended to a key with a tilde, and only when it is not the
 * plain card, so every key ever written down - a wish scope, a URL, a stored
 * slot - still reads exactly as it did. The default a set opens on is still
 * the plain printing, so Brilliant Stars still draws 216 tiles until somebody
 * asks for more. The database counts slots the same way; this file only has to
 * name and colour what it counts.
 */

/**
 * The canonical id for a printing, used as a map key and as part of a slot id.
 *
 * Stamps are sorted, because the order they were written in is not part of what
 * a printing IS - the same rule wish_scope_matches_printing follows in SQL, so
 * a scope and a slot cannot disagree over ['a','b'] versus ['b','a'].
 */
/**
 * The printing a set opens on.
 *
 * Every set has one: no subtype, no stamp, no foil - the card as it was simply
 * printed. Checked across the catalogue, it covers every card in every set,
 * which is what makes it usable as a default without curating 263 sets by hand.
 *
 * Opening on it rather than on all of them is the difference between Base Set
 * drawing 102 tiles and 408. The others are a tap away and the tile says how
 * many there are, so nothing is hidden - it is just not all shouted at once.
 */
export const MAIN_PRINTING_KEY = '|';

/**
 * The printing a set should open on: the ordinary one.
 *
 * Most sets have a printing with no subtype, no stamp and no foil, and that is
 * simply the card. Base Set does not - every one of its 102 cards exists only
 * as 1st Edition, Shadowless, Unlimited or a 1999-2000 copyright - so a rule
 * that only knows about the plain printing falls back to showing all 408, which
 * is the thing this is meant to avoid.
 *
 * So it is a ladder, and it ends where a collector would: Unlimited is the
 * standard Base Set card. It is the common one and the cheap one - $19 a card
 * against $109 for 1st Edition - which is exactly what makes it the right thing
 * to open on and the wrong thing to be shown without being told.
 *
 * @param {Array<{printing_subtype: string|null, printing_stamps: string[]}>} slots
 * @returns {string|null} a printing key, or null to show every printing
 */
export function standardPrintingKey(slots = []) {
  const keys = new Set();
  for (const s of slots) keys.add(slotPrintingKey(s));
  if (!keys.size) return null;
  if (keys.has(MAIN_PRINTING_KEY)) return MAIN_PRINTING_KEY;
  // Named in the order a collector would reach for them. A stamp is never
  // ordinary, so nothing carrying one is a candidate.
  for (const subtype of ['unlimited', '1999-2000-copyright', '1999-copyright']) {
    const k = printingKey(subtype, [], null);
    if (keys.has(k)) return k;
  }
  // Nothing recognisable as the plain card. Showing every printing is a better
  // answer than picking one arbitrarily and hiding the rest.
  return null;
}

export function printingKey(subtype, stamps, foil, type) {
  const list = [...(stamps || [])].filter(Boolean).sort();
  // Foil is appended rather than inserted, so every key written before ball
  // patterns became printings still reads the same. A key is in URLs.
  const base = `${subtype || ''}|${list.join(',')}`;
  const withFoil = foil ? `${base}|${foil}` : base;
  // Its own separator, so a finish can never be mistaken for a foil pattern in
  // a key that has no foil, and the plain card's key is unchanged.
  return isPlainFinish(type) ? withFoil : `${withFoil}~${type}`;
}

/** Whether a finish is simply "the card", which is the absence of a finish. */
export function isPlainFinish(type) {
  const t = String(type || '').toLowerCase();
  return !t || t === 'normal';
}

/** The key for a slot row as the view hands it over. */
export function slotPrintingKey(slot) {
  return printingKey(
    slot?.printing_subtype, slot?.printing_stamps, slot?.printing_foil, slot?.printing_type,
  );
}

/**
 * Words a person already reads on eBay and at a card shop.
 *
 * Deliberately the same vocabulary wish_set_subsets produces in SQL, so the row
 * on the Wish tab and the badge inside the grid say the same word about the same
 * printing. Never numbered: no "Part 2", no "Printing 3".
 */
export function printingLabel(subtype, stamps, foil, type) {
  const base = baseLabel(subtype, stamps, foil);
  if (isPlainFinish(type)) return base;
  const finish = FINISH_LABEL[String(type).toLowerCase()] || titleCase(type);
  // The finish names the printing when nothing else does, and qualifies the
  // name when something else already has: a Shadowless card that is also a
  // reverse is "Shadowless Reverse", not one or the other.
  return base === 'Standard' ? finish : `${base} ${finish}`;
}

function baseLabel(subtype, stamps, foil) {
  const list = (stamps || []).filter(Boolean);
  if (list.includes('1st-edition')) return '1st Edition';
  if (subtype === 'shadowless') return 'Shadowless';
  if (subtype === 'unlimited') return 'Unlimited';
  if (subtype === '1999-2000-copyright') return '4th Print';
  if (list.length > 0) return `${titleCase(list.join(' '))} Stamp`;
  if (subtype) return titleCase(subtype);
  // A foil pattern is printed into the card, so it is the printing's name when
  // nothing else claims it. Two ball patterns on one card would otherwise wear
  // the same word and be impossible to tell apart on the grid.
  if (foil) return FOIL_LABEL[foil] || titleCase(foil);
  return 'Standard';
}

// The words a person reads on eBay, not the words the catalogue stores.
const FINISH_LABEL = {
  reverse: 'Reverse Holo',
  holo: 'Holo',
  metal: 'Metal',
  lenticular: 'Lenticular',
};

const FOIL_LABEL = {
  masterball: 'Master Ball',
  pokeball: 'Poke Ball',
  duskball: 'Dusk Ball',
  loveball: 'Love Ball',
  friendball: 'Friend Ball',
  quickball: 'Quick Ball',
  cosmos: 'Cosmos',
  galaxy: 'Galaxy',
  energy: 'Energy',
  mirror: 'Mirror',
  'cracked-ice': 'Cracked Ice',
  tinsel: 'Tinsel',
  starlight: 'Starlight',
  league: 'League',
  gold: 'Gold',
};

/** A short form for the badge, where five columns leave very little room. */
export function printingShortLabel(subtype, stamps, foil, type) {
  const label = printingLabel(subtype, stamps, foil, type);
  return SHORT[label] || label;
}

const SHORT = {
  'Master Ball': 'Master',
  'Poke Ball': 'Poke Ball',
  'Cracked Ice': 'Ice',
  '1st Edition': '1st Ed',
  Shadowless: 'Shadowless',
  Unlimited: 'Unlimited',
  '4th Print': '4th Print',
  Standard: 'Standard',
  'Reverse Holo': 'Reverse',
  'Shadowless Reverse Holo': 'Shadowless Rev',
  'Unlimited Reverse Holo': 'Unltd Rev',
};

/**
 * The fallback palette, for a printing nobody has named yet.
 *
 * Chosen by a hash of the printing key rather than by position in a list, so a
 * set that gains a printing tomorrow does not recolour the ones already on
 * screen today.
 */
const PALETTE = ['#ec4899', '#14b8a6', '#eab308', '#6366f1', '#f97316', '#84cc16'];

const NAMED = {
  // Gold, because a 1st Edition stamp is the one a collector is hunting.
  '1st Edition': '#f59e0b',
  Shadowless: '#38bdf8',
  Unlimited: '#94a3b8',
  '4th Print': '#a78bfa',
  Standard: '#64748b',
  // The ball patterns take the colour of the ball, which is the one thing
  // somebody already knows before they read the word.
  'Master Ball': '#7c3aed',
  'Poke Ball': '#ef4444',
  // The finishes. A reverse is the one somebody is most often trying to tell
  // apart from the card beside it, so it gets the loudest of the three.
  'Reverse Holo': '#c084fc',
  Holo: '#2dd4bf',
};

export function printingColor(subtype, stamps, foil, type) {
  const label = printingLabel(subtype, stamps, foil, type);
  if (NAMED[label]) return NAMED[label];
  const key = printingKey(subtype, stamps, foil, type);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 100000;
  return PALETTE[hash % PALETTE.length];
}

/** Black on gold, white on everything darker. Contrast, not taste. */
export function printingTextColor(subtype, stamps, foil, type) {
  return printingColor(subtype, stamps, foil, type) === '#f59e0b' ? '#000000' : '#ffffff';
}

function titleCase(text) {
  return String(text)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The sentence the UI owes the person looking at four identical Charizards.
 *
 * Shown once, at the top of a grid that actually repeats art, rather than as a
 * caption on every tile. A note on four hundred tiles is not a disclosure, it
 * is a nag.
 */
/**
 * The one-line form, for the top of a grid where the full wording is one tap
 * away. The grid is what somebody came to look at, and the long version pushed
 * a third of a card row off the bottom of the screen to say it.
 */
export const ARTWORK_DISCLOSURE_SHORT = 'Repeated art is a set of printings. The badge tells them apart.';

// Written when every printing genuinely did share one picture. Since 09.17 the
// ones that look different - 1st Edition, Shadowless, the stamps, the ball
// patterns - carry their own. A holo and a reverse holo still share, because
// they are the same artwork with a different finish, so the disclosure is still
// true of most of the grid and is no longer true of all of it.
export const ARTWORK_DISCLOSURE = 'Printings that look the same share one piece of artwork, '
  + 'so repeated art here is a set of printings and not a mistake. Where a printing really '
  + 'looks different - a 1st Edition stamp, a Master Ball pattern - it has its own picture. '
  + 'The coloured badge on the top of each card is what tells them apart.';

/**
 * A printing as a URL parameter, or '' when there is nothing to say.
 *
 * The badge on a tile already carries the printing; this is how it travels
 * through the door. A parameter rather than a route segment because CardDetail
 * already reads `view`, `photo` and `b` the same way, and because a card with
 * no printing worth naming should produce the URL it always produced.
 *
 * @param {{subtype?: string|null, stamps?: string[]}|null} badge
 */
export function printingParam(badge) {
  if (!badge) return '';
  const key = printingKey(badge.subtype || null, badge.stamps || [], badge.foil || null, badge.type);
  // The plain printing's key is '|' - no subtype, no stamps. A card with
  // nothing to distinguish should produce the URL it always produced.
  if (!key || key === '|') return '';
  return `?printing=${encodeURIComponent(key)}`;
}

/**
 * A printing key back into words, for a screen that was handed one in a URL.
 *
 * The inverse of printingParam. An unrecognised key returns null rather than
 * throwing or inventing a label: a link that is bookmarked today and opened
 * after the printing vocabulary has moved on should show the card, not an
 * error.
 *
 * @param {string|null} key  e.g. "shadowless|1st-edition"
 * @returns {string|null} e.g. "Shadowless / 1st Edition Stamp"
 */
export function printingLabelFromKey(key) {
  if (!key || typeof key !== 'string' || key === '|') return null;
  // The finish rides after a tilde, so it comes off before the rest is split.
  const [axes, type] = key.split('~');
  const [subtype, stampList, foil] = (axes || '').split('|');
  const stamps = (stampList || '').split(',').filter(Boolean);
  if (!subtype && !stamps.length && !foil && !type) return null;
  return printingLabel(subtype || null, stamps, foil || null, type || null) || null;
}

/** The finish a printing key names, for a screen handed one in a URL. */
export function finishFromKey(key) {
  if (!key || typeof key !== 'string') return null;
  const type = key.split('~')[1];
  return type && !isPlainFinish(type) ? type : null;
}
